import { memo, useEffect, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { interludeApi, type InterludeClipConfig } from '../../lib/settingsApi.js'
import { formatDuration } from '../../lib/utils.js'
import { MissingFileBadge } from '../common/MissingFileBadge.js'
import { Spinner } from '../common/Spinner.js'

const CLIP_TYPE_LABELS: Record<InterludeClipConfig['clipType'], string> = {
  LOOP: 'Boucle',
  PROMO: 'Promo',
  COMING_NEXT: 'Coming Next',
  SLATE: 'Mire',
  EMERGENCY: 'Urgence',
}

/**
 * Ordre réel de comblement, tel que le moteur le calcule.
 *
 * `caspar.service.ts` retient les clips actifs triés par `priority`
 * décroissante, écarte le clip d'urgence du choix courant et ne s'y rabat que
 * si aucun autre n'est disponible. Ce panneau ne fait que rendre cet ordre
 * visible — il ne le configure pas : c'est le champ `priority` de chaque clip,
 * modifiable dans Réglages → Général, qui le détermine.
 */
export const GapFillStrategy = memo(function GapFillStrategy({ channelId }: { channelId: string | null }) {
  const [clips, setClips] = useState<InterludeClipConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!channelId) { setClips([]); return }
    let cancelled = false
    setLoading(true)
    interludeApi.list(channelId)
      .then(list => { if (!cancelled) { setClips(list); setError('') } })
      .catch(() => { if (!cancelled) setError('Clips d’interlude indisponibles') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId])

  const active = clips.filter(clip => clip.active)
  const ordered = [...active]
    .filter(clip => clip.clipType !== 'EMERGENCY')
    .sort((a, b) => b.priority - a.priority)
  const emergency = active.find(clip => clip.clipType === 'EMERGENCY') ?? null

  return (
    <section className="settings-gapfill" aria-label="Stratégie de comblement">
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Stratégie de comblement</h2>
          <p className="settings-view-description">
            Ordre dans lequel le moteur choisit un clip quand un trou s’ouvre à l’antenne.
            Il se règle par la priorité de chaque clip, pas ici.
          </p>
        </div>
      </div>

      {error && <div className="tp-feedback tp-feedback--error">{error}</div>}

      {loading ? (
        <p className="settings-gapfill__empty"><Spinner /> Chargement…</p>
      ) : active.length === 0 ? (
        <p className="settings-gapfill__empty">
          Aucun clip d’interlude actif : un trou d’antenne ne serait comblé par rien.
        </p>
      ) : (
        <ol className="settings-gapfill__list">
          {ordered.map((clip, index) => (
            <li key={clip.id} className="settings-gapfill__row">
              <span className="settings-gapfill__rank">{index + 1}</span>
              <span className="settings-gapfill__name">
                {clip.name} <MissingFileBadge presence={clip.presence} />
              </span>
              <span className="settings-gapfill__type">{CLIP_TYPE_LABELS[clip.clipType]}</span>
              <span className="settings-gapfill__duration">
                {clip.durationSec ? formatDuration(clip.durationSec) : '—'}
              </span>
              <span className="settings-gapfill__priority">priorité {clip.priority}</span>
            </li>
          ))}

          <li className="settings-gapfill__row is-emergency">
            <span className="settings-gapfill__rank" aria-hidden="true">
              <ShieldAlert size={13} />
            </span>
            <span className="settings-gapfill__name">
              {emergency ? emergency.name : 'Aucun clip d’urgence configuré'}
              {emergency && <> <MissingFileBadge presence={emergency.presence} /></>}
            </span>
            <span className="settings-gapfill__type">Dernier recours</span>
            <span className="settings-gapfill__duration">
              {emergency?.durationSec ? formatDuration(emergency.durationSec) : '—'}
            </span>
            <span className="settings-gapfill__priority">
              {emergency ? 'si rien d’autre n’est disponible' : 'à configurer'}
            </span>
          </li>
        </ol>
      )}
    </section>
  )
})
